import {
  ADAPTIVE_PROFILE_KEY,
  DEFAULT_ADAPTIVE_PROFILE,
  DEFAULT_USER_PREFERENCES,
  PREFERENCES_KEY,
  type AdaptivePlayerProfile,
  type AdaptiveProfileRepository,
  type PreferencesRepository,
  type UserPreferences,
} from '@texas42/app-model';
import type { Texas42Database } from '../db.js';
import { STORE_ADAPTIVE_PROFILES, STORE_PREFERENCES } from '../constants.js';
import { readEnvelopeOrNull, writeEnvelope } from '../envelope-io.js';
import type { WriteQueue } from '../write-queue.js';
import { computeChecksum } from '../checksum.js';
import { createEnvelope } from '../records.js';
import { StorageUnavailableError } from '../storage-errors.js';

interface PreferencesRecord {
  key: string;
  value: UserPreferences;
}

export function createIndexedDbPreferencesRepository(
  db: Texas42Database,
  queue: WriteQueue,
  defaults: UserPreferences = DEFAULT_USER_PREFERENCES,
): PreferencesRepository {
  return {
    async load() {
      const record = await readEnvelopeOrNull<PreferencesRecord>(
        db,
        STORE_PREFERENCES,
        PREFERENCES_KEY,
        'preferences',
        PREFERENCES_KEY,
      );
      return { ...defaults, ...(record?.value ?? {}) };
    },

    async save(value) {
      const record: PreferencesRecord = { key: PREFERENCES_KEY, value };
      await writeEnvelope(queue, db, STORE_PREFERENCES, record);
    },
  };
}

export function createIndexedDbAdaptiveProfileRepository(
  db: Texas42Database,
  queue: WriteQueue,
  emptyProfile: AdaptivePlayerProfile = DEFAULT_ADAPTIVE_PROFILE,
): AdaptiveProfileRepository {
  return {
    async load() {
      const profile = await readEnvelopeOrNull<AdaptivePlayerProfile>(
        db,
        STORE_ADAPTIVE_PROFILES,
        ADAPTIVE_PROFILE_KEY,
        'adaptiveProfile',
        ADAPTIVE_PROFILE_KEY,
      );
      return profile ?? { ...emptyProfile };
    },

    async save(profile) {
      await writeEnvelope(queue, db, STORE_ADAPTIVE_PROFILES, profile);
    },

    async reset() {
      await queue.enqueue(async () => {
        try {
          const checksum = await computeChecksum(emptyProfile);
          await db.put(STORE_ADAPTIVE_PROFILES, createEnvelope(emptyProfile, checksum));
        } catch (error) {
          throw new StorageUnavailableError('Unable to reset adaptive profile.', error);
        }
      });
    },
  };
}
